import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Mic } from "lucide-react";
import { SubHeader } from "@/components/SubHeader";
import { Reveal } from "@/components/Reveal";
import { API } from "@/lib/site";

const ytId = (url) => {
  const m = (url || "").match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
};

export default function EpisodeDetail() {
  const { slug } = useParams();
  const [episode, setEpisode] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    axios
      .get(`${API}/episodes/${slug}`)
      .then(({ data }) => {
        setEpisode(data);
        setStatus("ok");
        document.title = `${data.title} | Healthcare Connect`;
      })
      .catch(() => setStatus("missing"));
    return () => {
      document.title = "Healthcare Solutions | Connecting Healthcare. Creating Solutions.";
    };
  }, [slug]);

  const videoId = episode ? ytId(episode.youtube_url) : null;

  return (
    <div data-testid="episode-page" className="min-h-screen bg-white">
      <SubHeader crumb="Healthcare Connect" />
      <main className="mx-auto max-w-4xl px-4 pb-24 pt-32 sm:px-6">
        {status === "loading" && <p className="text-center text-sm text-[#6B8AA0]">Loading episode...</p>}
        {status === "missing" && (
          <div className="text-center">
            <h1 className="font-display text-2xl font-bold text-[#0B3C5C]">Episode not found</h1>
            <Link to="/connect" data-testid="episode-back-link" className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#4C8F2F] hover:text-[#5FA832]">
              <ArrowLeft className="h-4 w-4" /> Back to Healthcare Connect
            </Link>
          </div>
        )}
        {status === "ok" && episode && (
          <Reveal>
            <Link to="/connect" data-testid="episode-back-link" className="inline-flex items-center gap-2 text-xs font-semibold text-[#4A6B82] transition-colors hover:text-[#5FA832]">
              <ArrowLeft className="h-3.5 w-3.5" /> All Episodes
            </Link>

            <div className="mt-8 flex items-center gap-3">
              <span className="font-mono-x inline-flex items-center gap-2 rounded-full border border-teal-500/30 bg-[#5FA832]/10 px-4 py-1.5 text-[10px] uppercase tracking-[0.25em] text-[#5FA832]">
                <Mic className="h-3 w-3" /> Healthcare Connect
              </span>
              <span className="text-xs text-[#6B8AA0]">
                {new Date(episode.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}
              </span>
            </div>

            <h1 className="font-display mt-6 text-3xl font-extrabold leading-tight tracking-tight text-[#0B3C5C] sm:text-4xl">
              {episode.title}
            </h1>
            <p className="font-display mt-4 text-lg text-[#5FA832]">
              {episode.guest_name}
              {episode.guest_role && <span className="text-sm text-[#4A6B82]"> · {episode.guest_role}</span>}
            </p>

            {videoId ? (
              <div className="mt-8 overflow-hidden rounded-[2rem] border border-[#0B3C5C]/15 shadow-2xl shadow-black/40">
                <div className="aspect-video w-full">
                  <iframe
                    src={`https://www.youtube.com/embed/${videoId}`}
                    title={episode.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="h-full w-full"
                    data-testid="episode-video-embed"
                  />
                </div>
              </div>
            ) : episode.cover_image ? (
              <div className="mt-8 overflow-hidden rounded-[2rem] border border-[#0B3C5C]/15 shadow-2xl shadow-black/40">
                <img src={episode.cover_image} alt={episode.title} className="aspect-[21/9] w-full object-cover" />
              </div>
            ) : null}

            {episode.description && (
              <div className="mt-10 space-y-6" data-testid="episode-description">
                {episode.description.split(/\n{2,}/).map((para, i) => (
                  <p key={i} className="text-base leading-relaxed text-[#33586F]">{para}</p>
                ))}
              </div>
            )}

            <div className="mt-12 rounded-[2rem] border border-[#0B3C5C]/10 bg-gradient-to-br from-white to-[#EAF3F8] p-9 text-center">
              <h2 className="font-display text-xl font-bold text-[#0B3C5C]">Want to be featured on Healthcare Connect?</h2>
              <p className="mt-2 text-sm text-[#4A6B82]">Share your healthcare journey and insights with a wider audience.</p>
              <a
                href="/#enquiry"
                data-testid="episode-cta-guest"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#5FA832] px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-[#4C8F2F]"
              >
                Become a Featured Guest
              </a>
            </div>
          </Reveal>
        )}
      </main>
    </div>
  );
}
